/**
 * The product companies A2 runs creator programs for.
 *
 * Edit this list, not the JSX — the Brands section, the footer, the showcase
 * carousel and the public brief pages all read from it. Add the matching
 * creator brief in briefs.tsx.
 */

/**
 * Stable per-brand key. It is the `/brand/:brandName` URL segment, the key the
 * showcase videos are tagged with, and the key a brief is filed under, so it
 * must never change once a link to it has been sent to a creator.
 */
export type BrandSlug = 'mila' | 'hiding' | 'closed' | 'roxy' | 'sunday';

/** How the logo tile renders. */
export type LogoTone =
  /** Logo art needs a light plate behind it (dark or coloured marks). */
  | 'light'
  /** Logo art needs a dark plate behind it (light/white marks). */
  | 'dark'
  /** Self-contained app icon that already carries its own background. */
  | 'bare';

export interface Brand {
  slug: BrandSlug;
  name: string;
  /** Short category line shown opposite the logo. */
  tag: string;
  /** The brand's own one-liner — kept in their voice, not ours. */
  headline: string;
  description: string;
  url: string;
  logo: string;
  logoTone: LogoTone;
}

export const BRANDS: Brand[] = [
  {
    slug: 'mila',
    name: 'Mila.gg',
    tag: 'AI Productivity',
    headline: 'An actually good Microsoft Office.',
    description:
      'A reimagined document suite, built by people who care. Featured in The New York Times and trusted by 148,000+ users.',
    url: 'https://mila.gg',
    logo: '/brands/mila.png',
    logoTone: 'light',
  },
  {
    slug: 'hiding',
    name: 'Tryhiding.com',
    tag: 'Stealth AI',
    headline: 'Hidden AI that solves problems in real time.',
    description:
      'A privacy-first AI companion for professionals and students. 13,700+ customers, rated 4.95★.',
    url: 'https://tryhiding.com',
    logo: '/brands/hiding.png',
    logoTone: 'dark',
  },
  {
    slug: 'closed',
    name: 'Tryclosed.com',
    tag: 'AI Receptionist',
    headline: 'Never miss another appointment.',
    description:
      'An AI front desk that answers every call, books the appointment and follows up. 231 businesses served, 2,400+ appointments booked, 98% answer rate.',
    url: 'https://tryclosed.com',
    logo: '/brands/closed.svg',
    logoTone: 'bare',
  },
  {
    slug: 'roxy',
    name: 'Roxy.gg',
    tag: 'Developer Tools',
    headline: 'An open-source AI coding agent.',
    description:
      'A native desktop agent for macOS, Windows and Linux. Bring your own model, or use Roxy — pay-as-you-go inference at cost, no subscription.',
    url: 'https://roxy.gg',
    logo: '/brands/roxy.png',
    logoTone: 'bare',
  },
  {
    slug: 'sunday',
    name: 'Sunday Store',
    tag: 'Creator Commerce',
    headline: 'Your link in bio. Your course storefront.',
    description:
      'A link-in-bio storefront where creators can sell courses, digital products, and services alongside their content and social links.',
    url: 'https://sunday.store/',
    logo: '/brands/sunday.png',
    logoTone: 'bare',
  },
];

/** Reduces a URL segment or brand name to a comparable key. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Resolves a `/brand/:brandName` segment to a brand.
 *
 * Deliberately forgiving. This URL is pasted into DMs, Notion pages and email
 * signatures by people who are not looking at this file, so `/brand/mila`,
 * `/brand/Mila.gg` and `/brand/tryhiding` all have to land — a creator who
 * follows a link to a brief and gets a 404 simply does not make the video.
 */
export function findBrand(param: string | undefined): Brand | undefined {
  if (!param) return undefined;
  const key = normalise(param);
  if (!key) return undefined;

  return BRANDS.find((brand) => {
    if (brand.slug === key) return true;
    // "Mila.gg" → "milagg", and "mila" once the TLD is dropped.
    const name = normalise(brand.name);
    const hostname = new URL(brand.url).hostname.replace(/^www\./, '');
    return name === key || normalise(hostname) === key || normalise(hostname.split('.')[0]) === key;
  });
}
